import React, { createContext, useContext, useMemo } from "react";
import { WebsitePageSection } from "@/types";

interface WebPageFormContextProps {
  // Form sections - the single source of truth for all displayed data
  // Initialized with API data, updated when user edits
  pageData: Record<string, WebsitePageSection>;
  updateLocalSection: (sectionTag: string, updatedSection: Partial<WebsitePageSection>) => void;
  onSavePage?: () => Promise<void>;
  isLoading: boolean;
}

const WebPageFormContext = createContext<WebPageFormContextProps | undefined>(undefined);

/**
 * Hook to access webpage form context
 * Provides access to page data (form state) and update function
 */
export const useWebPageForm = () => {
  const context = useContext(WebPageFormContext);
  if (!context) {
    throw new Error('useWebPageForm must be used within WebPageFormProvider');
  }
  return context;
};

/**
 * Hook for page components to get section data
 * Returns the section data directly from form state (single source of truth)
 */
export const usePageSection = (sectionTag: string): WebsitePageSection | undefined => {
  const { pageData } = useWebPageForm();
  return pageData[sectionTag];
};

/**
 * Hook for page components to get all page data
 * Returns all sections from form state in the same structure as API
 */
export const usePageData = () => {
  const { pageData, isLoading } = useWebPageForm();
  
  return useMemo(() => {
    // Return data in the same structure as API response for compatibility
    return {
      data: {
        data: pageData
      },
      isLoading
    };
  }, [pageData, isLoading]);
};

interface WebPageFormProviderProps {
  children: React.ReactNode;
  pageData: Record<string, WebsitePageSection>;
  updateLocalSection: (sectionTag: string, updatedSection: Partial<WebsitePageSection>) => void;
  onSavePage?: () => Promise<void>;
  isLoading?: boolean;
}

export const WebPageFormProvider: React.FC<WebPageFormProviderProps> = ({
  children,
  pageData,
  updateLocalSection,
  onSavePage,
  isLoading = false
}) => {
  // Memoize context value to prevent unnecessary re-renders
  // Create new object reference when pageData changes
  const contextValue = React.useMemo(() => ({
    pageData: { ...pageData }, // Create new object reference to ensure React detects changes
    updateLocalSection,
    onSavePage,
    isLoading
  }), [pageData, updateLocalSection, onSavePage, isLoading]);

  return (
    <WebPageFormContext.Provider value={contextValue}>
      {children}
    </WebPageFormContext.Provider>
  );
};

